import { HTMLInputTypeAttribute, useState, forwardRef } from 'react';
import cn from 'classnames';
import SlashedEyeIcon from '@/shared/assets/icons/slashed-eye.svg';
import OpenedEyeIcon from '@/shared/assets/icons/opened-eye.svg';
import { Svg } from '@/shared/ui/Svg';
import { FieldWrapper } from '../FieldWrapper';
import { BaseFieldProps, getFieldStyles, getFieldWithIconStyles } from '../BaseField';
interface TextfieldProps extends BaseFieldProps {
  type?: HTMLInputTypeAttribute;
  readOnly?: boolean;
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const Textfield = forwardRef<HTMLInputElement, TextfieldProps>((props, ref) => {
  const {
    className,
    wrapperClassName,
    label,
    placeholder,
    type = 'text',
    error,
    showError = false,
    name,
    labelClassName,
    disabled,
    readOnly,
    value,
    defaultValue,
    onChange,
    ...rest
  } = props;
  const [showPassword, setShowPassword] = useState(false);

  const getAutoCompleteValue = (name: string, type: string): string => {
    if (type === 'password') return 'current-password';

    const autoCompleteMap: Record<string, string> = {
      email: 'email',
      name: 'name',
      firstName: 'given-name',
      lastName: 'family-name',
      address: 'street-address',
      city: 'address-level2',
      postcode: 'postal-code',
    };

    return autoCompleteMap[name] || 'off';
  };

  if (type === 'password') {
    return (
      <FieldWrapper
        label={label}
        error={error}
        showError={showError}
        fieldId={name}
        labelClassName={labelClassName}
        className={wrapperClassName}
      >
        <div className="w-full relative">
          <input
            id={name}
            name={name}
            ref={ref}
            className={getFieldWithIconStyles(error, className, disabled, readOnly)}
            aria-invalid={showError && error ? true : undefined}
            aria-describedby={showError && error ? `${name}-error` : undefined}
            type={showPassword ? 'text' : 'password'}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            value={value}
            defaultValue={defaultValue}
            onChange={onChange}
            {...rest}
          />
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 focus:outline-none"
            onClick={() => setShowPassword((prev) => !prev)}
            type="button"
            aria-label="toggle password visibility"
          >
            {showPassword ? (
              <Svg
                Icon={SlashedEyeIcon}
                className={cn(
                  'text-2xl pointer-events-none',
                  error ? 'text-red-500' : 'text-default-400',
                )}
                width={24}
                height={24}
              />
            ) : (
              <Svg
                Icon={OpenedEyeIcon}
                className={cn(
                  'text-2xl pointer-events-none',
                  error ? 'text-red-500' : 'text-default-400',
                )}
                width={24}
                height={24}
              />
            )}
          </button>
        </div>
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper
      label={label}
      error={error}
      showError={showError}
      fieldId={name}
      labelClassName={labelClassName}
      className={wrapperClassName}
    >
      <input
        id={name}
        name={name}
        ref={ref}
        className={getFieldStyles(error, className, disabled, readOnly)}
        aria-invalid={showError && error ? true : undefined}
        aria-describedby={showError && error ? `${name}-error` : undefined}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        autoComplete={getAutoCompleteValue(name, type)}
        {...rest}
      />
    </FieldWrapper>
  );
});

Textfield.displayName = 'Textfield';
